import {
  DigitalOceanAgent,
  DigitalOceanAgentsResponse,
  CreateDigitalOceanAgentRequest,
  UpdateDigitalOceanAgentRequest,
} from "@/types/breeze/AIAgent";
import { BaseService } from "../BaseService";

export interface AgentApiKey {
  id: string;
  name: string;
  api_key?: string;
  created_at: string;
  status: string;
}

export interface CreateAgentApiKeyRequest {
  agent_uuid: string;
  name: string;
}

export interface AgentApiKeysResponse {
  api_keys: AgentApiKey[];
}

export class AIAgentService extends BaseService {
  public async getDigitalOceanAgents(): Promise<DigitalOceanAgentsResponse> {
    return this.getRequest("/ai-agents");
  }

  public async getDigitalOceanAgent(
    agentId: string,
  ): Promise<DigitalOceanAgent> {
    return this.getRequest(`/ai-agents/${agentId}`);
  }

  public async createDigitalOceanAgent(
    request: CreateDigitalOceanAgentRequest,
  ): Promise<DigitalOceanAgent> {
    return this.postRequest(
      "/ai-agents/create",
      request,
      "AI agent created successfully",
    );
  }

  public async updateDigitalOceanAgent(
    agentId: string,
    request: UpdateDigitalOceanAgentRequest,
  ): Promise<DigitalOceanAgent> {
    return this.putRequest(
      `/ai-agents/${agentId}`,
      request,
      "AI agent updated successfully",
    );
  }

  public async deleteDigitalOceanAgent(agentId: string): Promise<void> {
    return this.deleteRequest(
      `/ai-agents/${agentId}`,
      "AI agent deleted successfully",
    );
  }

  // API Key Management
  public async getAgentApiKeys(agentId: string): Promise<AgentApiKey[]> {
    try {
      const response = (await this.getRequest(
        `/ai-agents/${agentId}/api-keys`,
      )) as AgentApiKeysResponse;
      return response.api_keys || [];
    } catch (error) {
      console.error("Error fetching agent API keys:", error);
      return [];
    }
  }

  public async createAgentApiKey(
    agentId: string,
    name: string,
  ): Promise<AgentApiKey> {
    console.log("Creating API key for agent:", agentId);

    const response = (await this.postRequest(
      `/ai-agents/${agentId}/api-keys`,
      {
        agent_uuid: agentId,
        name: name,
      } as CreateAgentApiKeyRequest,
      "API key created successfully",
    )) as Record<string, unknown>;

    console.log("API key creation response received");

    // DigitalOcean returns the key with 'secret_key' field according to API docs
    // Also check for 'api_key' as fallback
    const apiKey: AgentApiKey = {
      id: (response.id || response.uuid || "") as string,
      name: (response.name || "") as string,
      api_key: (response.secret_key ||
        response.api_key ||
        response.key ||
        "") as string,
      created_at: (response.created_at || new Date().toISOString()) as string,
      status: (response.status || "active") as string,
    };

    console.log("API key parsed successfully");
    return apiKey;
  }

  public async deleteAgentApiKey(
    agentId: string,
    keyId: string,
  ): Promise<void> {
    return this.deleteRequest(
      `/ai-agents/${agentId}/api-keys/${keyId}`,
      "API key deleted successfully",
    );
  }

  // Get or create a default API key for an agent
  public async ensureAgentApiKey(
    agentId: string,
    agentName: string,
  ): Promise<string | null> {
    try {
      // First, try to get existing API keys
      const existingKeys = await this.getAgentApiKeys(agentId);

      // Look for an active key
      const activeKey = existingKeys.find(
        (key) => key.status === "active" && key.api_key,
      );

      if (activeKey?.api_key) {
        return activeKey.api_key;
      }

      // If no active key exists, create a new one
      const newKey = await this.createAgentApiKey(
        agentId,
        `Anythink-${agentName}-ChatKey-${
          new Date().toISOString().split("T")[0]
        }`,
      );

      return newKey.api_key || null;
    } catch (error) {
      console.error("Error ensuring agent API key:", error);
      return null;
    }
  }
}

export const aiAgentService = new AIAgentService();
